import { HubCoreService, AlfredService, Loader, Notification } from '@hub/shared'
import onCountNotificationsChange from '@observables/count-notifications.observable'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import './notifications-feed.component.scoped.scss'
import { SqInfinityScroll } from '@squidit/react-css'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  notificationClicked?: (notification: Notification) => any
  elementToScroll?: string
}

export default ({ className = '', style, notificationClicked, elementToScroll = '' }: Props) => {
  const [t, i18n] = useTranslation('globalMain')
  const hubCoreService = useMemo(() => new HubCoreService(), [])
  const alfredService = useMemo(() => new AlfredService(), [])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [page, setPage] = useState<number>(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loadingPagination, setLoadingPagination] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const navigate = useNavigate()

  useEffect(() => {
    if (((loading && page === 1) || !loading) && !loadingPagination && page > 0 && hasMore) {
      setLoadingPagination(!loading)
      hubCoreService.getNotifications(page, 15).then((notifications) => {
        if (notifications?.length) {
          setNotifications((old) => [...old, ...notifications])
          const validToUpdate = notifications?.filter((n) => n?.status === 'sent')?.map((n) => n?.id)
          onCountNotificationsChange?.next(onCountNotificationsChange?.value - validToUpdate?.length)
          if (validToUpdate?.length) {
            alfredService.updateNotificationsStatus(validToUpdate, 'read')
          }
        } else {
          setHasMore(false)
        }
        setLoadingPagination(false)
        setLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hubCoreService, alfredService])

  const parseIcons = (template: string): string => {
    if (template?.includes('campaign')) {
      return 'fa-bullhorn'
    }
    if (template?.includes('user-register')) {
      return 'fa-address-card'
    }
    if (template?.includes('token')) {
      return 'fa-link'
    }
    if (template?.includes('payment')) {
      return 'fa-wallet'
    }
    return 'fa-bookmark'
  }

  const handleNotification = async (notification: Notification) => {
    if (notification?.status !== 'read') {
      await alfredService.updateNotificationsStatus([notification.id], 'read')
    }
    if (notification?.link) {
      const url = new URL(notification.link)
      if (notificationClicked && typeof notificationClicked === 'function') {
        notificationClicked(notification)
      }
      if (window.location.hostname.includes(url.hostname)) {
        const search = new URLSearchParams(url.search)
        search.set('notification', notification.id)
        navigate(`${url.pathname?.replace('/legacy', '')}?${search.toString()}`)
      } else {
        window.location.href = notification.link
      }
    }
  }

  let content = (
    <div className="wrapper-loader text-center my-3">
      <Loader size="big" />
    </div>
  )
  if (!notifications?.length && !loading) {
    content = (
      <div className="no-notifications">
        <p>{t('emptyNotification')}</p>
      </div>
    )
  }

  if (notifications?.length && !loading) {
    content = (
      <SqInfinityScroll
        elementToScrollId={elementToScroll}
        loading={loadingPagination}
        onLoadMore={() => (!loadingPagination ? setPage(page + 1) : null)}
        endMessage={' '}
        hasMore={hasMore}
      >
        {notifications?.map((notification, index) => {
          return (
            <div key={notification.id + '-' + index}>
              <button type="button" onClick={() => handleNotification(notification)} className={`notification ${notification.status}`}>
                {notification?.status === 'sent' ? <i className="fa-solid fa-circle-exclamation exclamation" /> : null}
                <figure>
                  <i className={`fa-regular ${parseIcons(notification.template)} icon-notification`} />
                </figure>
                <article>
                  <h5>{notification.title}</h5>
                  <p>{notification.message}</p>
                  <p className="date">
                    {new Date(notification.date).toLocaleDateString(i18n.language, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </p>
                </article>
              </button>
              {notifications?.length > index + 1 ? <hr /> : null}
            </div>
          )
        })}
      </SqInfinityScroll>
    )
  }

  return (
    <div className={`notifications-feed-wrapper ${className}`} style={style}>
      {content}
    </div>
  )
}
